import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { useEarlyPredictionsQuery, useLeaderboardQuery } from "@/api/vercel";
import { format, formatDistanceStrict, isPast, parseISO } from "date-fns";
import { useAbandonedMeetings } from "@/hooks/useAbandonedMeetings";
import logger from "@/utils/logger";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useTracking } from "@/components/TrackingProvider";
import AbandonedLabel from "@/components/abandoned-label";
import RaceCountdown from "./RaceCountdown";
import MedalIcon from "./icons/medal-icon";
import TrophyIcon from "./icons/trophy-icon";

const NextToJumpCarousel = forwardRef(({ onDataLoad }, ref) => {
  const today = format(new Date(), "dd-MM-yyyy");
  const raceContainerRef = useRef(null);
  const selectedRaceRef = useRef(null);

  // Add this ref for the auto-select race functionality
  const hasAutoSelectedRef = useRef(false);

  // State variables
  const [urlTrack, setUrlTrack] = useState(null);
  const [urlRace, setUrlRace] = useState(null);
  const [selectedRace, setSelectedRace] = useState(null);
  const [nextToJumpRace, setNextToJumpRace] = useState(null);
  const [isNextToJumpActive, setIsNextToJumpActive] = useState(false);
  const [currentRace, setCurrentRace] = useState(null); // Track the current race that's in progress
  const [allRacesFinished, setAllRacesFinished] = useState(false); // Track if all races are finished
  const [races, setRaces] = useState([]);
  const [hasRacesToday, setHasRacesToday] = useState(false);
  const [countdownTimes, setCountdownTimes] = useState({});

  // Ref to track if refresh is needed to prevent multiple refreshes
  const refreshNeededRef = useRef(false);

  // Add tracking hook
  const { trackRaceFilterClick, trackNavigation } = useTracking();

  // Get abandoned meetings data
  const { isTrackAbandoned, getAbandonmentReason } =
    useAbandonedMeetings(today);

  // Add state to track the current URL path
  const [currentPath, setCurrentPath] = useState(window.location.pathname);

  // Expose navigation methods to parent components via ref
  useImperativeHandle(ref, () => ({
    navigateToNextRace: () => navigateToAdjacentRace(1),
    navigateToPreviousRace: () => navigateToAdjacentRace(-1),
    canNavigateNext: () => {
      if (!selectedRace || races.length === 0) return false;
      const currentIndex = races.findIndex(
        (race) => race.id === selectedRace.id,
      );
      return currentIndex < races.length - 1;
    },
    canNavigatePrevious: () => {
      if (!selectedRace || races.length === 0) return false;
      const currentIndex = races.findIndex(
        (race) => race.id === selectedRace.id,
      );
      return currentIndex > 0;
    },
    // Add a method to get the next available race for navigation
    _getNextAvailableRace: () => {
      if (races.length === 0) return null;

      // First try to get the next race to jump (first non-completed race)
      let nextRace = races.find((race) => !race.isCompleted);

      // If all races are completed, use the last race chronologically
      if (!nextRace) {
        const chronologicalRaces = [...races].sort(
          (a, b) => a.marketStartTime - b.marketStartTime,
        );
        nextRace = chronologicalRaces[chronologicalRaces.length - 1];
      }

      return nextRace || null;
    },
  }));

  // Navigate to next or previous race
  const navigateToAdjacentRace = (direction) => {
    if (!selectedRace || races.length === 0) return;

    const currentIndex = races.findIndex((race) => race.id === selectedRace.id);
    if (currentIndex === -1) return;

    const nextIndex = currentIndex + direction;
    if (nextIndex >= 0 && nextIndex < races.length) {
      const raceToNavigateTo = races[nextIndex];
      handleRaceSelect(raceToNavigateTo);
    }
  };

  // Add an effect to listen for URL changes and navigation events
  useEffect(() => {
    const handleUrlChange = () => {
      // Update current path
      setCurrentPath(window.location.pathname);
      
      // Get current URL parameters
      const url = new URL(window.location.href);
      const trackParam = url.searchParams.get("track");
      const raceParam = url.searchParams.get("race");

      // Only process URL params on the todays-tips page
      if (window.location.pathname === "/todays-tips" && trackParam && raceParam) {
        // Format track name to match data format (replace hyphens with spaces and capitalize)
        const formattedTrack = trackParam
          .split("-")
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(" ");

        // Find matching race
        if (races.length > 0) {
          const matchingRace = races.find(
            (race) =>
              race.track.toLowerCase().replace(/\s+/g, "-") === formattedTrack.toLowerCase().replace(/\s+/g, "-") &&
              race.raceNumber.toString() === raceParam,
          );

          if (matchingRace && (!selectedRace || selectedRace.id !== matchingRace.id)) {
            setSelectedRace(matchingRace);
          }
        }
      } else if (window.location.pathname !== "/todays-tips") {
        // If we're not on the todays-tips page, clear the selection
        setSelectedRace(null);
      }
    };

    // Set up event listeners for various navigation events
    window.addEventListener("popstate", handleUrlChange);
    window.addEventListener("urlchange", handleUrlChange);
    
    // Custom event for client-side navigation
    window.addEventListener("navigationend", handleUrlChange);
    
    // Initial check
    handleUrlChange();

    return () => {
      window.removeEventListener("popstate", handleUrlChange);
      window.removeEventListener("urlchange", handleUrlChange);
      window.removeEventListener("navigationend", handleUrlChange);
    };
  }, [races]);

  // Update ref when selectedRace changes
  useEffect(() => {
    if (selectedRaceRef.current !== selectedRace) {
      selectedRaceRef.current = selectedRace;
    }
  }, [selectedRace]);

  // Function to refresh data without page reload
  const refreshData = useCallback(() => {
    // Only refresh if not already refreshing
    if (refreshNeededRef.current) {
      refreshNeededRef.current = false;

      // Use the query's refetch method to get latest data
      if (refetchLeaderboard) {
        refetchLeaderboard();
      }

      // Dispatch custom event to notify other components that data is being refreshed
      window.dispatchEvent(new CustomEvent("lexi-data-refresh"));
    }
  }, []);

  // Fetch race data
  const {
    data: { data: leaderboardQueryData } = {},
    isError: isLeaderboardError,
    isLoading: isLeaderboardLoading,
    refetch: refetchLeaderboard,
  } = useLeaderboardQuery({ date: today, collection: "predictions" });
  // Process the leaderboard data to extract race information
  useEffect(() => {
    if (leaderboardQueryData && Object.keys(leaderboardQueryData).length > 0) {
      try {
        const currentDate = new Date();
        const processed = Object.entries(leaderboardQueryData)
          .map(([key, race]) => {
            if (!race || !race.marketStartTime) {
              // Handle incomplete race data
              return null;
            }

            const marketStartTime = parseISO(race.marketStartTime);
            const isCompleted = isPast(marketStartTime);

            // Calculate seconds until race starts for precise countdown
            const secondsToStart = Math.floor(
              (marketStartTime - currentDate) / 1000,
            );

            // Calculate minutes until race starts (for existing code)
            const minutesToStart = Math.floor(secondsToStart / 60);

            // Determine if race is currently in progress (started within last 10 minutes)
            const isInProgress =
              isCompleted && minutesToStart > -10 && !race.hasResults;

            const timeToStart = formatDistanceStrict(
              marketStartTime,
              currentDate,
              {
                addSuffix: false,
              },
            );

            // Enhanced results data
            let hasResults = false;
            let winsCount = 0;
            let placesCount = 0;

            // Check if race has results
            if (race.betfairResults) {
              hasResults =
                race.betfairResults.winner ||
                (race.betfairResults.placeGetters &&
                  race.betfairResults.placeGetters.length > 0);

              // If we have firstFourData (top tips) and results, analyse the performance
              if (
                hasResults &&
                race.firstFourData &&
                Array.isArray(race.firstFourData) &&
                race.firstFourData.length >= 2
              ) {
                const topTwoTips = race.firstFourData.slice(0, 2);

                // Check for wins among top 2 tips
                if (race.betfairResults.winner) {
                  const winnerSelectionId =
                    race.betfairResults.winner.selectionId?.toString();
                  // Check if any of the top 2 tips won
                  winsCount = topTwoTips.filter(
                    (tip) => tip.selectionId?.toString() === winnerSelectionId,
                  ).length;
                }

                // Check for places among top 2 tips
                if (
                  race.betfairResults.placeGetters &&
                  race.betfairResults.placeGetters.length > 0
                ) {
                  // Get selection IDs of place getters
                  const placeSelectionIds =
                    race.betfairResults.placeGetters.map((place) =>
                      place.selectionId?.toString(),
                    );

                  // Count tips that placed but didn't win
                  placesCount = topTwoTips.filter((tip) => {
                    const tipId = tip.selectionId?.toString();
                    // Count as place only if it's not already counted as a win
                    return !winsCount ||
                      tipId !==
                        race.betfairResults.winner.selectionId?.toString()
                      ? placeSelectionIds.includes(tipId)
                      : false;
                  }).length;
                }
              }
            }

            return {
              id: key,
              track: race.track,
              raceNumber: race.race,
              marketStartTime,
              isCompleted,
              isInProgress,
              timeToStart,
              minutesToStart,
              secondsToStart, // Add seconds to start for precise countdown
              hasResults,
              winsCount, // Number of wins in top 2 tips
              placesCount, // Number of places in top 2 tips
              // Add any other relevant race info
            };
          })
          .filter((race) => race !== null); // Filter out any null entries

        // Sort races by start time (completed races first, then closest to start)
        const sortedRaces = processed.sort((a, b) => {
          if (a.isCompleted === b.isCompleted) {
            return a.marketStartTime - b.marketStartTime;
          }
          return a.isCompleted ? -1 : 1;
        });

        // Store current races to compare before updating state
        const currentRaces = [...races];
        const hasRacesChanged =
          currentRaces.length !== sortedRaces.length ||
          JSON.stringify(currentRaces) !== JSON.stringify(sortedRaces);
        // Only update if races have changed
        if (hasRacesChanged) {
        
          setRaces(sortedRaces);
          setHasRacesToday(sortedRaces.length > 0);

          // Check if all races are finished
          const areAllRacesFinished =
            sortedRaces.length > 0 &&
            sortedRaces.every((race) => race.isCompleted);
          setAllRacesFinished(areAllRacesFinished);

          // Find the next race to jump (first non-completed race)
          const nextRace = sortedRaces.find((race) => !race.isCompleted);

          // Only update if next race has changed
          if (
            !nextToJumpRace ||
            !nextRace ||
            nextToJumpRace.id !== nextRace.id
          ) {
            setNextToJumpRace(nextRace || null);
          }

          // Find current race (race currently in progress, if any)
          const raceInProgress = sortedRaces.find((race) => race.isInProgress);

          // Only update if current race has changed
          if (
            !currentRace ||
            !raceInProgress ||
            currentRace.id !== raceInProgress.id
          ) {
            setCurrentRace(raceInProgress || null);
          }
        }

        // Check if there's a race matching URL parameters - only run once per URL change
        if (urlTrack && urlRace && sortedRaces.length > 0 && !selectedRace) {
          const raceFromUrl = sortedRaces.find(
            (race) =>
              race.track.toLowerCase() === urlTrack.toLowerCase() &&
              race.raceNumber.toString() === urlRace.toString(),
          );

          if (raceFromUrl) {
            setSelectedRace(raceFromUrl);
            if (nextToJumpRace) {
              setIsNextToJumpActive(raceFromUrl.id === nextToJumpRace.id);
            }
          }
        }

        // Notify parent component about race data availability
        if (onDataLoad) {
          onDataLoad(sortedRaces.length > 0);
        }
      } catch (error) {
        console.error("Error processing race data:", error);
        setRaces([]);
        setHasRacesToday(false);
        setAllRacesFinished(false);
      }
    }
  }, [leaderboardQueryData, onDataLoad, urlTrack, urlRace]);

  // Scroll to the next to jump race
  useEffect(() => {
    if (nextToJumpRace && raceContainerRef.current) {
      const container = raceContainerRef.current;
      const nextRaceButton = container.querySelector(
        `[data-race-id="${nextToJumpRace.id}"]`,
      );

      // Only scroll to next-to-jump if no race is selected
      if (nextRaceButton && !selectedRace) {
        // Add a small delay to ensure the DOM is fully rendered
        setTimeout(() => {
          nextRaceButton.scrollIntoView({
            behavior: "auto",
            block: "nearest",
            inline: "start",
          });
        }, 100);
      }
    }
  }, [nextToJumpRace, races, selectedRace]);

  // Scroll to the selected race and ensure it's on the left side of the viewport
  useEffect(() => {
    if (selectedRace && raceContainerRef.current) {
      const container = raceContainerRef.current;
      const selectedRaceButton = container.querySelector(
        `[data-race-id="${selectedRace.id}"]`,
      );

      if (selectedRaceButton) {
        // Add a small delay to ensure the DOM is fully rendered
        setTimeout(() => {
          // Scroll the selected race to be visible on the left
          selectedRaceButton.scrollIntoView({
            behavior: "smooth",
            block: "nearest",
            inline: "start",
          });
        }, 100);
      }
    }
  }, [selectedRace]);

  const handleRaceSelect = (race) => {
    // Set selected race before updating URL to ensure proper state sync
    setSelectedRace(race);

    // Track this race filter click
    const isNextToJump =
      nextToJumpRace && nextToJumpRace.id === race.id && !race.isCompleted;
    const isInProgress =
      currentRace && currentRace.id === race.id && !race.hasResults;

    trackRaceFilterClick(
      race.track,
      race.raceNumber,
      isNextToJump,
      isInProgress,
    );

    // Check if we're already on the todays-tips page or need to navigate there
    const isOnTodaysTipsPage = window.location.pathname === "/todays-tips";

    // Format parameters for URL
    const formattedTrackName = race.track.replace(/\s+/g, "-").toLowerCase();
    const formattedRaceNumber = race.raceNumber.toString();

    if (isOnTodaysTipsPage) {
      // If already on the todays-tips page, just update URL parameters
      const url = new URL(window.location.href);
      url.searchParams.set("track", formattedTrackName);
      url.searchParams.set("race", formattedRaceNumber);

      // Update URL without causing page reload
      window.history.pushState({}, "", url);

      // Track navigation
      trackNavigation(url.pathname + url.search, "race_filter");

      // Dispatch custom event to notify components about the URL change
      window.dispatchEvent(
        new CustomEvent("urlchange", {
          detail: {
            pathname: "/todays-tips",
            search: `?track=${formattedTrackName}&race=${formattedRaceNumber}`,
            track: formattedTrackName,
            race: formattedRaceNumber,
          },
        }),
      );

      // Dispatch additional custom event for navigation end
      window.dispatchEvent(new CustomEvent("navigationend"));

      // Dispatch popstate event to ensure all handlers are notified
      window.dispatchEvent(new PopStateEvent("popstate"));
    } else {
      // We need to navigate to the todays-tips page with parameters
      const todaysTipsPath = `/todays-tips?track=${formattedTrackName}&race=${formattedRaceNumber}`;

      // Track navigation
      trackNavigation(todaysTipsPath, "race_filter");

      // Use React Router's navigate if available - never directly modify window.location.href
      // as that causes a full page refresh and flickering
      if (window.navigate) {
        window.navigate(todaysTipsPath);
        // Dispatch custom navigationend event after navigation
        setTimeout(() => window.dispatchEvent(new CustomEvent("navigationend")), 50);
      } else if (
        window.clientSideRouter &&
        typeof window.clientSideRouter.navigate === "function"
      ) {
        window.clientSideRouter.navigate(todaysTipsPath);
        // Dispatch custom navigationend event after navigation
        setTimeout(() => window.dispatchEvent(new CustomEvent("navigationend")), 50);
      } else {
        // As a last resort, use history pushState and then dispatch events
        const newUrl = `${window.location.origin}${todaysTipsPath}`;
        window.history.pushState({}, "", newUrl);

        // Let React Router handle the navigation
        window.dispatchEvent(new PopStateEvent("popstate"));
        
        // Dispatch custom navigationend event
        window.dispatchEvent(new CustomEvent("navigationend"));
      }
    }
  };

  // Render skeleton loading animation for race buttons
  const renderRaceSkeletons = () => {
    return (
      <div className="race-filter-container">
        <div className="race-filter-inner-container">
          {[...Array(5)].map((_, index) => (
            <div
              key={`skeleton-${index}`}
              className="h-10 w-28 rounded-full bg-gray-200 animate-pulse"
            />
          ))}
        </div>
      </div>
    );
  };

  // Modify the getRaceStatusStyles function to use the current path state
  const getRaceStatusStyles = (race) => {
    // Get current URL parameters
    const url = new URL(window.location.href);
    const trackParam = url.searchParams.get("track");
    const raceParam = url.searchParams.get("race");

    // Check if this race matches the URL parameters
    const formattedTrackParam = trackParam
      ? trackParam
          .split("-")
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(" ")
      : "";

    const isSelectedFromUrl =
      trackParam &&
      raceParam &&
      race.track.toLowerCase() === formattedTrackParam.toLowerCase() &&
      race.raceNumber.toString() === raceParam;

    // Only consider the race selected if we're on the todays-tips page
    const isOnTodaysTipsPage = currentPath === "/todays-tips";
    
    // Determine if selected based on either component state or URL parameters
    const isSelected =
      isOnTodaysTipsPage && ((selectedRace && selectedRace.id === race.id) || isSelectedFromUrl);
    
    const isNextJump =
      nextToJumpRace && nextToJumpRace.id === race.id && !race.isCompleted;

    // Only consider a race "in progress" if it doesn't have results yet
    const isInProgress =
      currentRace && currentRace.id === race.id && !race.hasResults;

    let classes = "race-filter-button ";

    // Base styling based on race status
    if (isSelected) {
      classes += "race-selected ";
    }
    if (race.isCompleted) {
      classes += "race-completed ";
    } else if (isNextJump) {
      classes += "race-next-to-jump ";
    } else {
      classes += "race-upcoming ";
    }

    // Add border for currently in-progress race
    if (isInProgress) {
      classes += "race-in-progress ";
    }

    return classes;
  };

  // Add a useEffect to handle countdown completion
  useEffect(() => {
    // Handler for countdown completion
    const handleCountdownComplete = () => {
      // Only process if we have races data
      if (races.length === 0) return;

      // Create a copy of the current races
      const currentRaces = [...races];

      // Get the current date to calculate race statuses
      const currentDate = new Date();

      // Update race statuses based on current time
      const updatedRaces = currentRaces.map((race) => {
        // Check if race is completed based on current time
        const isCompleted = isPast(race.marketStartTime);

        // Calculate seconds until race starts for precise countdown
        const secondsToStart = Math.floor(
          (race.marketStartTime - currentDate) / 1000,
        );

        // Calculate minutes until race starts
        const minutesToStart = Math.floor(secondsToStart / 60);

        // Determine if race is in progress (started within last 10 minutes)
        const isInProgress =
          isCompleted && minutesToStart > -10 && !race.hasResults;

        return {
          ...race,
          isCompleted,
          isInProgress,
          secondsToStart,
          minutesToStart,
        };
      });

      // Find the next race to jump (first non-completed race)
      const nextRace = updatedRaces.find((race) => !race.isCompleted);

      // Find current race (race currently in progress, if any)
      const raceInProgress = updatedRaces.find((race) => race.isInProgress);

      // Update states
      if (nextRace && (!nextToJumpRace || nextToJumpRace.id !== nextRace.id)) {
        setNextToJumpRace(nextRace);
      }

      if (
        raceInProgress &&
        (!currentRace || currentRace.id !== raceInProgress.id)
      ) {
        setCurrentRace(raceInProgress);
      }
    };

    // Set up event listener for the custom countdown complete event
    window.addEventListener("countdown-complete", handleCountdownComplete);

    // Also set a regular interval to update race statuses
    const intervalId = setInterval(handleCountdownComplete, 30000); // Check every 30 seconds

    return () => {
      window.removeEventListener("countdown-complete", handleCountdownComplete);
      clearInterval(intervalId);
    };
  }, [races, nextToJumpRace, currentRace, selectedRace]);

  // Modify the auto-select effect to also check current page path
  useEffect(() => {
    // Only run this if we have races loaded and no race is selected
    if (races.length > 0 && !selectedRace && !hasAutoSelectedRef.current) {
      const isOnTodaysTipsPage = window.location.pathname === "/todays-tips";

      // Only auto-select if we're on the today's tips page AND there are no URL params
      if (isOnTodaysTipsPage) {
        const url = new URL(window.location.href);
        const trackParam = url.searchParams.get("track");
        const raceParam = url.searchParams.get("race");

        // ONLY auto-select on first page load with no params
        // This preserves the initial page load behavior but won't redirect for race updates
        if (!trackParam || !raceParam) {
          hasAutoSelectedRef.current = true;

          let raceToSelect;

          // First try to use next to jump
          if (nextToJumpRace) {
            raceToSelect = nextToJumpRace;
          }
          // If no next to jump (all races completed), use the last chronological race
          else if (races.length > 0) {
            // All races are completed, find the last one chronologically
            const sortedByTime = [...races].sort(
              (a, b) => a.marketStartTime - b.marketStartTime,
            );
            raceToSelect = sortedByTime[sortedByTime.length - 1];
          }

          // If we found a race to select, update the URL and select it
          if (raceToSelect) {
            // Use setTimeout to break the render cycle and prevent infinite updates
            setTimeout(() => {
              handleRaceSelect(raceToSelect);
            }, 0);
          }
        }
      }
    }

    // Reset the flag when races or nextToJumpRace change
    if (hasAutoSelectedRef.current && (races.length === 0 || selectedRace)) {
      hasAutoSelectedRef.current = false;
    }
  }, [races, nextToJumpRace, selectedRace]);

  // Helper function to check if a race is abandoned
  const isRaceAbandoned = (race) => {
    // First check if the entire track is abandoned
    if (isTrackAbandoned(race.track)) {
      return true;
    }

    // Then check individual race status if available
    return (
      race.isAbandoned === true ||
      race.marketStatus === "ABANDONED" ||
      (race.marketStatus && race.marketStatus.includes("ABANDONED"))
    );
  };

  // Set up countdown timer for races under 60 seconds
  useEffect(() => {
    if (!races.length) return;

    // Find races that are close to starting (less than 60 seconds)
    const racesUnder60Seconds = races.filter(
      (race) =>
        !race.isCompleted &&
        race.secondsToStart >= 0 &&
        race.secondsToStart < 60,
    );

    if (racesUnder60Seconds.length === 0) return;

    // Initialize countdown times if not already set
    const initialCountdowns = {};
    racesUnder60Seconds.forEach((race) => {
      if (countdownTimes[race.id] === undefined) {
        initialCountdowns[race.id] = race.secondsToStart;
      }
    });

    if (Object.keys(initialCountdowns).length > 0) {
      setCountdownTimes((prev) => ({ ...prev, ...initialCountdowns }));
    }

    // Set up interval to count down every second
    const intervalId = setInterval(() => {
      setCountdownTimes((prev) => {
        const newTimes = { ...prev };
        let needsRefresh = false;

        racesUnder60Seconds.forEach((race) => {
          if (newTimes[race.id] !== undefined && newTimes[race.id] > 0) {
            newTimes[race.id] = newTimes[race.id] - 1;

            // If any race reaches 0, mark for refresh
            if (newTimes[race.id] === 0) {
              refreshNeededRef.current = true;
              needsRefresh = true;
            }
          }
        });

        // If refresh needed, trigger it
        if (needsRefresh) {
          setTimeout(refreshData, 100);
        }

        return newTimes;
      });
    }, 1000);

    return () => clearInterval(intervalId);
  }, [races, countdownTimes, refreshData]);

  return (
    <div className="race-filter-wrapper">
      {/* Loading skeleton */}
      {isLeaderboardLoading && renderRaceSkeletons()}

      {/* Error state */}
      {isLeaderboardError && (
        <div className="text-center py-8 px-4">
          <p className="text-red-500 mb-2">Error loading race data</p>
          <p className="text-gray-600">Please try again later.</p>
        </div>
      )}

      {/* Race Navigation Bar - Only show if:
          1. There are races today
          2. Data is loaded (not loading/error)
          3. Not all races have finished
      */}
      {!isLeaderboardLoading && !isLeaderboardError && hasRacesToday && (
        <div
          ref={raceContainerRef}
          className="race-filter-container px-4 pt-4 pb-2 bg-white border-t border-gray-200 overflow-x-auto overflow-y-hidden"
        >
          <div className="race-filter-inner-container">
            {races.map((race) => {
              // Check if race is abandoned
              const abandoned = isRaceAbandoned(race);

              return (
                <button
                  key={race.id}
                  data-race-id={race.id}
                  type="button"
                  className={`${getRaceStatusStyles(race)} ${abandoned ? "opacity-75" : ""}`}
                  onClick={() => handleRaceSelect(race)}
                  data-gtm-element-id={`todays-tips_button-${race.id}`}
                >
                  <span className="race-label">
                    {race.track} R{race.raceNumber}
                  </span>

                  {/* Time to start - Always render this element, but only show content when needed */}
                  <span className="race-time-indicator">
                    {nextToJumpRace &&
                      nextToJumpRace.id === race.id &&
                      !race.isCompleted && (
                        <RaceCountdown
                          raceId={race.id}
                          marketStartTime={race.marketStartTime.toISOString()}
                          variant="compact"
                          showSeconds={race.secondsToStart < 60}
                        />
                      )}
                  </span>

                  {/* In-progress indicator - Always render with conditional content */}
                  <span className="race-active-indicator">
                    {currentRace &&
                      currentRace.id === race.id &&
                      !race.hasResults &&
                      "⚡"}
                  </span>

                  {/* Result indicators - Always include in DOM for stability */}
                  <span
                    className="result-indicators"
                    style={
                      race.isCompleted &&
                      race.hasResults &&
                      (race.winsCount > 0 || race.placesCount > 0)
                        ? { visibility: "visible" }
                        : { visibility: "hidden" }
                    }
                  >
                    {/* Trophy for wins */}
                    {race.winsCount > 0 ? (
                      <TrophyIcon
                        className="result-indicator result-indicator-winner"
                        title="Winner"
                      />
                    ) : null}

                    {/* Medals for places (1 or 2) */}
                    {race.placesCount > 0 ? (
                      <span className="result-place-indicators-container">
                        {[...Array(race.placesCount)].map((_, i) => (
                          <MedalIcon
                            key={`place-${i}`}
                            fill="#9ca3af"
                            className="result-indicator result-indicator-place"
                          />
                        ))}
                      </span>
                    ) : null}

                    {/* No wins/places indicator */}
                    {race.winsCount === 0 && race.placesCount === 0 ? (
                      <span
                        className="result-indicator result-indicator-fail"
                        title="No wins or places"
                      >
                        X
                      </span>
                    ) : null}
                  </span>

                  {/* Add abandoned overlay if race is abandoned */}
                  {abandoned && (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <AbandonedLabel
                        className="z-10 text-sm px-3 py-1"
                        reason={getAbandonmentReason(race.track)}
                        showReason={true}
                      />
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Day complete message - Only show if all races are finished */}
      {/* {!isLeaderboardLoading &&
        !isLeaderboardError &&
        hasRacesToday &&
        allRacesFinished && (
          <div className="text-center py-2 px-4 bg-gray-50 text-sm text-gray-500 italic border-t border-gray-200">
            <p>All races completed for today</p>
          </div>
        )} */}
    </div>
  );
});

export default NextToJumpCarousel;
